import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./lib/theme";
import { preloadDictionary } from "./i18n";
import "./styles/globals.css";

/**
 * 從網址取出語系。
 *
 * 網址的形狀是 /:market/:locale/...，而 App 也會做同樣的解析 ——
 * 這裡重複一次是因為我們需要在 React 掛載**之前**就知道要載
 * 哪一本字典，而那時候還沒有任何元件跑過。
 *
 * 解析錯了不會出事：preloadDictionary 認不得就什麼都不做，
 * Provider 之後照樣會自己載（只是會閃一下）。
 */
function localeFromUrl(): string {
  const segments = window.location.pathname.split("/").filter(Boolean);
  return segments[1] ?? "en";
}

const container = document.getElementById("root");

if (!container) {
  throw new Error("Mount point #root not found");
}

/**
 * 掛載順序很重要。
 *
 * 這裡刻意「先 render 再清空 SEO 骨架」：
 * 如果反過來（先清空再 render），只要 render 失敗，
 * 使用者看到的就是全白 —— 連 Worker 注入的內容都沒了。
 * 現在就算 React 掛掉，ErrorBoundary 也會接手畫出錯誤畫面。
 */
const shell = container.querySelector("#seo-shell");
const root = createRoot(container);

/*
  字典先載好再掛載。

  字典改成動態 import 之後，Provider 的初始值只能是英文 ——
  那是唯一同步拿得到的一本。結果是每一個非英語使用者的第一次
  繪製都是英文內容，然後才換成他的語言，而語言選單同時顯示著
  「繁體中文」。兩者對不起來，使用者的結論是「切換反了」。

  在這裡等，那段時間和 React 自己的初始化重疊，而且畫面上仍然是
  Worker 注入的 SEO 骨架（有標題、有描述），不是空白。

  英文使用者完全不受影響：en 在主 bundle 裡，preloadDictionary
  會立刻回傳（一個已 resolve 的 promise）。

  ── 為什麼是 .then() 而不是 top-level await ──

  建置目標包含 chrome87 / firefox78 / safari14，那些版本沒有
  top-level await。用了會讓 vite build **直接失敗** ——
  而失敗的建置配上 wrangler deploy，部署出去的是上一次的 dist：
  一個看起來成功、實際上什麼都沒更新的部署。

  我就是這樣把一版舊的 dist 推上正式站的。
*/
void preloadDictionary(localeFromUrl()).then(() => {
  root.render(
    <StrictMode>
      <ErrorBoundary>
        <ThemeProvider>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </ThemeProvider>
      </ErrorBoundary>
    </StrictMode>,
  );

  // React 接手後才移除爬蟲用的骨架
  shell?.remove();

  /*
    收掉開場的預載畫面（見 index.html 的 #boot）。

    ── 為什麼要等一格 ──────────────────────────────────────────

    root.render() 回來的時候，React 只是**排定**了這次繪製，
    畫面上還沒有東西。這時候立刻淡出遮罩，使用者會看到一瞬間的
    空白 —— 而那一瞬間正好接在他剛剛盯著載入動畫之後，特別顯眼。

    requestAnimationFrame 排在下一次繪製之後，那時候第一畫已經
    送出去了。用兩層是因為第一層仍然可能落在同一批工作裡。

    ── 為什麼淡出而不是直接移除 ────────────────────────────────

    直接移除是硬切，而硬切會讓人以為畫面「跳」了一下。350ms 的
    淡出（transition 寫在 index.html）銜接得上，而且它是純 CSS ——
    不會因為主執行緒正忙著掛載而卡住。

    prefers-reduced-motion 的使用者那條 transition 是關掉的，
    他們拿到的是即時切換，這是對的。
  */
  const boot = document.getElementById("boot");
  if (boot) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        boot.classList.add("boot-done");
        /*
          等淡出跑完再真的移除。

          用 transitionend 的話，在 prefers-reduced-motion
          （transition: none）下永遠不會觸發 —— 那個情況下會留下
          一層 opacity:0 但仍然擋住點擊的遮罩。計時器兩種情況都會到。
        */
        setTimeout(() => boot.remove(), 400);
      });
    });
  }
});
